import React, { useMemo, useState, useCallback, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Loader2,
  X,
  ChevronLeft,
  ChevronRight,
  MessageSquare,
} from "lucide-react";
import { UnconfirmedPartsDialog } from "./partsReview/UnconfirmedPartsDialog";
import { JSONObject } from "@/components/jsonSchemaForm/types";
import { useAuthStore, useHasPermission } from "@/store/authStore";
import { canUserApproveTask } from "@/lib/auth-utils";
import { Permission } from "@forgepoint/shared";
import { UserReviewForm } from "./UserReviewForm";
import type { RichActionData } from "./helpers/richActionHelpers";

export interface UserReviewPanelProps {
  richActionData: RichActionData;
  onApprove?: (taskId: string) => void;
  onDeny?: (taskId: string) => void;
  isApproving?: boolean;
  isDenying?: boolean;
  onOpenChange?: (open: boolean) => void;
  isChatCollapsed?: boolean;
  onToggleChat?: () => void;
  isMobile?: boolean;
  sdkThreadId?: string;
  sdkMessageId?: string;
}

export const UserReviewPanel: React.FC<UserReviewPanelProps> = ({
  richActionData,
  onApprove,
  onDeny,
  isApproving,
  isDenying,
  onOpenChange,
  isChatCollapsed,
  onToggleChat,
  isMobile,
  sdkThreadId,
  sdkMessageId,
}) => {
  const user = useAuthStore((state) => state.user);
  const canApproveAllTasks = useHasPermission(Permission.TASKS_MANAGE);
  const canApprove = canUserApproveTask(
    user,
    richActionData.task,
    canApproveAllTasks,
  );

  const [confirmedParts, setConfirmedParts] = useState<Set<number>>(new Set());
  const [showUnconfirmedDialog, setShowUnconfirmedDialog] = useState(false);
  const [userReviewFormArray, setUserReviewFormArray] = useState<JSONObject[]>(
    [],
  );
  const [selectedIndex, setSelectedIndex] = useState<number>(0);

  const requiresReview = richActionData.task?.status === "PENDING_REVIEW";

  const userReviewFormData = useMemo(() => {
    if (richActionData.richActionType !== "user_review_action") {
      return null;
    }

    try {
      const formJson = richActionData.actionArguments.formJson;
      if (typeof formJson !== "string") {
        return null;
      }
      const parsed = JSON.parse(formJson);
      if (Array.isArray(parsed)) {
        return parsed as JSONObject[];
      }
      return null;
    } catch {
      return null;
    }
  }, [richActionData]);

  useEffect(() => {
    if (userReviewFormData) {
      setUserReviewFormArray(userReviewFormData);
    }
  }, [userReviewFormData]);

  const totalPartsCount = useMemo(() => {
    return userReviewFormArray.length;
  }, [userReviewFormArray.length]);

  const unconfirmedParts = useMemo(() => {
    if (totalPartsCount === 0) {
      return [];
    }

    return userReviewFormArray
      .map((item, index) => ({ index, item }))
      .filter(({ index }) => !confirmedParts.has(index));
  }, [totalPartsCount, userReviewFormArray, confirmedParts]);

  const handleApprove = useCallback(
    (taskId: string) => {
      if (unconfirmedParts.length > 0) {
        setShowUnconfirmedDialog(true);
        return;
      }
      onApprove?.(taskId);
    },
    [unconfirmedParts, onApprove],
  );

  const handleConfirmationChange = useCallback(
    (newConfirmedParts: Set<number>) => {
      setConfirmedParts(newConfirmedParts);
    },
    [],
  );

  const handleNavigateToPart = useCallback((index: number) => {
    setSelectedIndex(index);
  }, []);

  const progressPercentage = useMemo(() => {
    if (totalPartsCount === 0) {
      return 0;
    }
    return (confirmedParts.size / totalPartsCount) * 100;
  }, [totalPartsCount, confirmedParts.size]);

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-3 h-16 relative">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {!isMobile && onToggleChat && (
              <Button
                variant="outline"
                size="sm"
                onClick={onToggleChat}
                className="gap-2"
              >
                <MessageSquare className="h-4 w-4" />
                {isChatCollapsed ? "Show Chat" : "Hide Chat"}
              </Button>
            )}
          </div>

          {totalPartsCount > 0 && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => setSelectedIndex(Math.max(0, selectedIndex - 1))}
                disabled={selectedIndex === 0}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm text-muted-foreground tabular-nums min-w-[60px] text-center">
                {selectedIndex + 1} of {totalPartsCount}
              </span>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() =>
                  setSelectedIndex(
                    Math.min(totalPartsCount - 1, selectedIndex + 1),
                  )
                }
                disabled={selectedIndex === totalPartsCount - 1}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}

          <div className="flex items-center gap-2">
            {requiresReview &&
              richActionData.task &&
              richActionData.task.status === "PENDING_REVIEW" &&
              canApprove &&
              onApprove &&
              onDeny && (
                <div className="flex gap-2">
                  <Button
                    className="bg-green-600 hover:bg-green-700 text-white"
                    onClick={() => handleApprove(richActionData.task!.id)}
                    disabled={isApproving || isDenying}
                  >
                    {isApproving ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Approve
                      </>
                    ) : (
                      <>Approve</>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => onDeny(richActionData.task!.id)}
                    disabled={isApproving || isDenying}
                  >
                    {isDenying ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Cancel
                      </>
                    ) : (
                      <>Cancel</>
                    )}
                  </Button>
                </div>
              )}
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => onOpenChange?.(false)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-border">
          {totalPartsCount > 0 && (
            <div
              className="h-full bg-red-600 transition-all duration-300 ease-out"
              style={{ width: `${progressPercentage}%` }}
            />
          )}
        </div>
      </div>

      <div className="flex-1 overflow-auto scrollbar-transparent">
        <UserReviewForm
          richActionData={richActionData}
          isApproving={Boolean(isApproving)}
          isDenying={Boolean(isDenying)}
          onApprove={onApprove}
          onDeny={onDeny}
          onConfirmationChange={handleConfirmationChange}
          externalSelectedIndex={selectedIndex}
          onSelectedIndexChange={handleNavigateToPart}
          sdkThreadId={sdkThreadId}
          sdkMessageId={sdkMessageId}
        />
        <UnconfirmedPartsDialog
          open={showUnconfirmedDialog}
          onOpenChange={setShowUnconfirmedDialog}
          unconfirmedParts={unconfirmedParts}
          onNavigateToPart={handleNavigateToPart}
        />
      </div>
    </div>
  );
};
